import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ContactCard } from "@/components/crm/ContactCard";
import { Loader2, Search, UserPlus, Link2, MessageCircle, BookUser, RefreshCw, ShieldCheck, ShieldOff } from "lucide-react";
import { CATEGORIES, INTERESTS, STATUSES, catOf, consentInfo, fmtDate, labelOf } from "@/lib/crm";

const ALL = "__all__";

function NewContactDialog({ open, onOpenChange, onCreated }) {
  const [f, setF] = useState({ name: "", whatsapp: "", email: "", categories: ["altro"], interests: [], notes: "" });
  const [saving, setSaving] = useState(false);
  const toggle = (k, v) => setF((s) => {
    let next = s[k].includes(v) ? s[k].filter((x) => x !== v) : [...s[k], v];
    if (k === "categories") {
      if (v !== "altro" && next.includes(v)) next = next.filter((x) => x !== "altro");
      if (next.length === 0) next = ["altro"];
    }
    return { ...s, [k]: next };
  });
  const save = async () => {
    if (!f.name.trim()) { toast.error("Inserisci il nome"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/contacts", f);
      toast.success("Contatto creato"); onOpenChange(false);
      setF({ name: "", whatsapp: "", email: "", categories: ["altro"], interests: [], notes: "" });
      onCreated?.(data.contact);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Creazione fallita"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg border-border bg-brema-card" data-testid="new-contact-dialog">
        <DialogHeader>
          <DialogTitle className="text-foreground">Nuovo contatto</DialogTitle>
          <DialogDescription>Scheda base (es. cliente finale). Se il numero esiste già in un archivio, la creazione viene bloccata per evitare duplicati.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1"><Label className="text-xs">Nome *</Label><Input value={f.name} onChange={(e) => setF((s) => ({ ...s, name: e.target.value }))} data-testid="new-contact-name" /></div>
            <div className="space-y-1"><Label className="text-xs">Numero WhatsApp</Label><Input value={f.whatsapp} onChange={(e) => setF((s) => ({ ...s, whatsapp: e.target.value }))} placeholder="+39 348 1234567" data-testid="new-contact-whatsapp" /></div>
          </div>
          <div className="space-y-1"><Label className="text-xs">E-mail</Label><Input value={f.email} onChange={(e) => setF((s) => ({ ...s, email: e.target.value }))} data-testid="new-contact-email" /></div>
          <div className="space-y-1">
            <Label className="text-xs">Categorie</Label>
            <div className="flex flex-wrap gap-1.5">{CATEGORIES.map((c) => <button key={c.value} type="button" onClick={() => toggle("categories", c.value)} data-testid={`new-contact-cat-${c.value}`} className={`rounded-full border px-2.5 py-1 text-xs font-medium ${f.categories.includes(c.value) ? `${c.cls} border-transparent` : "border-border text-muted-foreground"}`}>{c.label}</button>)}</div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Interessi</Label>
            <div className="flex flex-wrap gap-1.5">{INTERESTS.map((i) => <button key={i.value} type="button" onClick={() => toggle("interests", i.value)} data-testid={`new-contact-int-${i.value}`} className={`rounded-full border px-2.5 py-1 text-xs font-medium ${f.interests.includes(i.value) ? "border-transparent bg-brand/15 text-brand" : "border-border text-muted-foreground"}`}>{i.label}</button>)}</div>
          </div>
          <div className="space-y-1"><Label className="text-xs">Note</Label><Textarea value={f.notes} onChange={(e) => setF((s) => ({ ...s, notes: e.target.value }))} className="min-h-[70px]" data-testid="new-contact-notes" /></div>
          <Button onClick={save} disabled={saving} className="w-full rounded-full bg-primary font-semibold text-primary-foreground" data-testid="new-contact-save">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <UserPlus className="mr-2 h-4 w-4" />} Crea contatto
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RelinkDialog({ open, onOpenChange, onApplied }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [createMissing, setCreateMissing] = useState(false);
  const [applying, setApplying] = useState(false);
  const load = () => { setLoading(true); api.get("/contacts/relink-preview").then(({ data }) => setData(data)).catch(() => setData({ threads: [], summary: {} })).finally(() => setLoading(false)); };
  useEffect(() => { if (open) load(); }, [open]);
  const apply = async () => {
    setApplying(true);
    try {
      const { data: r } = await api.post("/contacts/relink-apply", { create_missing: createMissing });
      toast.success(`Collegate ${r.threads_linked} conversazioni${r.contacts_created ? `, create ${r.contacts_created} schede base` : ""}`);
      load(); onApplied?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Operazione fallita"); }
    finally { setApplying(false); }
  };
  const STATE = { unique: { label: "Corrispondenza univoca", cls: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300" }, ambiguous: { label: "Ambiguo · conferma manuale", cls: "bg-amber-500/15 text-amber-700 dark:text-amber-300" }, none: { label: "Numero sconosciuto", cls: "bg-slate-500/15 text-slate-700 dark:text-slate-300" } };
  const s = data?.summary || {};
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto border-border bg-brema-card" data-testid="relink-dialog">
        <DialogHeader>
          <DialogTitle className="text-foreground">Verifica conversazioni WhatsApp senza contatto</DialogTitle>
          <DialogDescription>Anteprima delle corrispondenze sugli archivi: nulla viene modificato finché non applichi. Vengono collegate SOLO le corrispondenze univoche; i casi ambigui restano da confermare nell'Inbox. Nessun dato esistente viene sovrascritto.</DialogDescription>
        </DialogHeader>
        {loading || !data ? <div className="py-10 text-center text-muted-foreground"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div> : (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2 text-xs" data-testid="relink-summary">
              <span className={`rounded-full px-2.5 py-1 font-semibold ${STATE.unique.cls}`}>Univoche: {s.unique || 0}</span>
              <span className={`rounded-full px-2.5 py-1 font-semibold ${STATE.ambiguous.cls}`}>Ambigue: {s.ambiguous || 0}</span>
              <span className={`rounded-full px-2.5 py-1 font-semibold ${STATE.none.cls}`}>Sconosciute: {s.none || 0}</span>
            </div>
            {data.threads.length === 0 ? <p className="py-6 text-center text-sm text-muted-foreground" data-testid="relink-empty">Tutte le conversazioni WhatsApp sono già collegate a un contatto.</p> : (
              <div className="max-h-[45vh] space-y-1.5 overflow-y-auto">
                {data.threads.map((t) => (
                  <div key={t.thread_key} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-brema-bg px-3 py-2 text-sm" data-testid={`relink-row-${t.thread_key}`}>
                    <div className="min-w-0">
                      <span className="font-semibold text-foreground">{t.from_name || t.from_phone}</span>
                      <span className="ml-2 text-xs text-muted-foreground">{t.from_phone} · {t.count} msg · {fmtDate(t.last_at)}</span>
                      {t.match && <div className="text-xs text-muted-foreground">→ {t.match.type_label}: <b className="text-foreground">{t.match.name}</b></div>}
                      {t.state === "ambiguous" && <div className="text-xs text-muted-foreground">Candidati: {t.candidates.map((c) => `${c.name} (${c.type_label})`).join(", ")}</div>}
                    </div>
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${STATE[t.state].cls}`}>{STATE[t.state].label}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-3">
              <label className="flex items-center gap-2 text-xs text-foreground">
                <Checkbox checked={createMissing} onCheckedChange={(v) => setCreateMissing(!!v)} data-testid="relink-create-missing" /> Crea una scheda base («Altro / Da classificare») per i numeri sconosciuti ({s.none || 0})
              </label>
              <Button onClick={apply} disabled={applying || ((s.unique || 0) === 0 && !(createMissing && (s.none || 0) > 0))} className="rounded-full bg-primary font-semibold text-primary-foreground disabled:opacity-40" data-testid="relink-apply-btn">
                {applying ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Link2 className="mr-2 h-4 w-4" />}
                Applica: {s.unique || 0} collegamenti univoci{createMissing && (s.none || 0) > 0 ? ` + ${s.none} ${s.none === 1 ? "scheda base" : "schede base"}` : ""}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Contacts() {
  const [q, setQ] = useState("");
  const [category, setCategory] = useState(ALL);
  const [interest, setInterest] = useState(ALL);
  const [status, setStatus] = useState(ALL);
  const [consent, setConsent] = useState(ALL);
  const [waOnly, setWaOnly] = useState(false);
  const [data, setData] = useState({ contacts: [], total: 0, counts: {} });
  const [loading, setLoading] = useState(true);
  const [limit, setLimit] = useState(100);
  const [selected, setSelected] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [relinkOpen, setRelinkOpen] = useState(false);

  const load = () => {
    setLoading(true);
    const p = new URLSearchParams({ limit: String(limit) });
    if (q.trim()) p.set("q", q.trim());
    if (category !== ALL) p.set("category", category);
    if (interest !== ALL) p.set("interest", interest);
    if (status !== ALL) p.set("status", status);
    if (consent !== ALL) p.set("consent", consent);
    if (waOnly) p.set("wa_only", "true");
    api.get(`/contacts?${p.toString()}`).then(({ data }) => setData(data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { const h = setTimeout(load, q ? 300 : 0); return () => clearTimeout(h); }, [q, category, interest, status, consent, waOnly, limit]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="contacts-page">
      <PageHeader eyebrow="CRM WhatsApp" title="Contatti / Rubrica"
        subtitle="Rubrica unificata su Rivenditori, Ambassador, Testate e contatti WhatsApp: una sola scheda per persona, categorie multiple, interessi e consenso commerciale."
        actions={
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" onClick={() => setRelinkOpen(true)} className="rounded-full border border-border bg-transparent font-medium text-foreground hover:bg-brema-hover" data-testid="contacts-relink-btn"><Link2 className="mr-2 h-4 w-4" /> Verifica thread WhatsApp</Button>
            <Button onClick={() => setNewOpen(true)} className="rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90" data-testid="contacts-new-btn"><UserPlus className="mr-2 h-4 w-4" /> Nuovo contatto</Button>
          </div>
        } />

      <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="contacts-filters">
        <div className="relative min-w-[240px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome o numero…" className="pl-9" data-testid="contacts-search" />
        </div>
        <Select value={category} onValueChange={setCategory}><SelectTrigger className="w-[170px]" data-testid="contacts-filter-category"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>Tutte le categorie</SelectItem>{CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent></Select>
        <Select value={interest} onValueChange={setInterest}><SelectTrigger className="w-[150px]" data-testid="contacts-filter-interest"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>Tutti gli interessi</SelectItem>{INTERESTS.map((i) => <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>)}</SelectContent></Select>
        <Select value={status} onValueChange={setStatus}><SelectTrigger className="w-[160px]" data-testid="contacts-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>Tutti gli stati</SelectItem>{STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent></Select>
        <Select value={consent} onValueChange={setConsent}><SelectTrigger className="w-[170px]" data-testid="contacts-filter-consent"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>Consenso: tutti</SelectItem><SelectItem value="granted">Consenso attivo</SelectItem><SelectItem value="none">Nessun consenso</SelectItem><SelectItem value="revoked">Revocato</SelectItem></SelectContent></Select>
        <Button variant="secondary" size="sm" onClick={() => setWaOnly((v) => !v)} data-testid="contacts-wa-only"
          className={`rounded-full border border-border text-xs ${waOnly ? "bg-selected text-selected-foreground hover:bg-selected/90" : "bg-transparent text-foreground hover:bg-brema-hover"}`}>
          <MessageCircle className="mr-1.5 h-3.5 w-3.5" /> Solo con WhatsApp
        </Button>
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground" data-testid="contacts-counts">
        <span className="font-semibold text-foreground" data-testid="contacts-total">{data.total} contatti</span>
        {CATEGORIES.map((c) => <span key={c.value} className={`rounded-full px-2 py-0.5 ${c.cls}`}>{c.label}: {data.counts?.[c.value] || 0}</span>)}
      </div>

      {loading ? <div className="py-16 text-center text-muted-foreground"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>
        : data.contacts.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border bg-brema-card/50 p-12 text-center text-muted-foreground" data-testid="contacts-empty"><BookUser className="mx-auto mb-3 h-8 w-8 opacity-50" /> Nessun contatto con questi filtri.</div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-border bg-brema-card">
            <table className="w-full text-sm" data-testid="contacts-table">
              <thead className="text-left text-xs text-muted-foreground">
                <tr className="border-b border-border">
                  <th className="px-4 py-3 font-medium">Contatto</th>
                  <th className="px-4 py-3 font-medium">Categorie</th>
                  <th className="px-4 py-3 font-medium">WhatsApp / E-mail</th>
                  <th className="px-4 py-3 font-medium">Interessi</th>
                  <th className="px-4 py-3 font-medium">Stato</th>
                  <th className="px-4 py-3 font-medium">Consenso</th>
                  <th className="px-4 py-3 font-medium">Messaggi</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {data.contacts.map((c) => {
                  const ci = consentInfo(c.consent);
                  return (
                    <tr key={`${c.type}-${c.id}`} className="border-b border-border/60 last:border-0 hover:bg-brema-hover/50" data-testid={`contact-row-${c.type}-${c.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-foreground" data-testid="contact-row-name">{c.name || "—"}</div>
                        <div className="text-[11px] text-muted-foreground">{c.type_label}{c.city ? ` · ${c.city}` : ""}{c.handle ? ` · @${String(c.handle).replace(/^@/, "")}` : ""}</div>
                      </td>
                      <td className="px-4 py-3"><div className="flex flex-wrap gap-1">{c.categories.map((k) => <span key={k} className={`whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-medium ${catOf(k).cls}`}>{catOf(k).label}</span>)}</div></td>
                      <td className="px-4 py-3 text-xs">
                        <div className={c.whatsapp ? "text-foreground" : "text-muted-foreground"} data-testid="contact-row-phone">{c.whatsapp || "— numero"}</div>
                        <div className="truncate text-muted-foreground" title={c.email}>{c.email || "—"}</div>
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">{c.interests.length ? c.interests.map((i) => labelOf(INTERESTS, i)).join(", ") : "—"}</td>
                      <td className="px-4 py-3 text-xs text-foreground">{labelOf(STATUSES, c.status)}</td>
                      <td className="px-4 py-3"><span className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-semibold ${ci.cls}`}>{c.consent?.status === "granted" ? <ShieldCheck className="h-3 w-3" /> : <ShieldOff className="h-3 w-3" />} {ci.label}</span></td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">{c.wa_count ? <span className="inline-flex items-center gap-1 text-[#128C4A] dark:text-[#25D366]"><MessageCircle className="h-3 w-3" /> {c.wa_count}</span> : "—"}{c.last_message_at ? <div>{fmtDate(c.last_message_at)}</div> : null}</td>
                      <td className="px-4 py-3 text-right"><Button size="sm" variant="secondary" onClick={() => setSelected(c)} className="h-8 rounded-full border border-border bg-transparent text-xs text-foreground hover:bg-brema-hover" data-testid="contact-row-open-btn">Scheda</Button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {data.total > data.contacts.length && (
              <div className="border-t border-border p-3 text-center">
                <Button variant="secondary" size="sm" onClick={() => setLimit((l) => l + 100)} className="rounded-full border border-border bg-transparent text-xs text-foreground" data-testid="contacts-load-more"><RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Mostra altri ({data.total - data.contacts.length})</Button>
              </div>
            )}
          </div>
        )}

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto border-border bg-brema-card" data-testid="contact-dialog">
          <DialogHeader>
            <DialogTitle className="text-foreground">{selected?.name || "Scheda contatto"}</DialogTitle>
            <DialogDescription>{selected?.type_label} · modifica diretta sull'anagrafica esistente, senza duplicati.</DialogDescription>
          </DialogHeader>
          {selected && <ContactCard ctype={selected.type} cid={selected.id} onSaved={(c) => { setSelected((s) => (s ? { ...s, name: c.name } : s)); load(); }} />}
        </DialogContent>
      </Dialog>
      <NewContactDialog open={newOpen} onOpenChange={setNewOpen} onCreated={load} />
      <RelinkDialog open={relinkOpen} onOpenChange={setRelinkOpen} onApplied={load} />
    </div>
  );
}
